/**
 * Auth0 Token Provider
 *
 * Bridges React (where the Auth0 hook lives) and plain utility functions
 * (apiRequest, WebSocket services) that cannot call React hooks.
 *
 * Auth0's getAccessTokenSilently() is designed to be called before every
 * API request. It returns cached tokens instantly if valid, or silently
 * refreshes via refresh token if expired. The SDK handles concurrency
 * internally with lock-based deduplication (Web Locks API / localStorage fallback).
 *
 * The stored function reference is safe to call outside React -- it's a closure
 * over the Auth0Client singleton, not a React hook. This is the same pattern
 * Auth0's Angular SDK uses with its built-in authHttpInterceptorFn.
 */

import * as Sentry from '@sentry/react';
import { useOrgStore } from '@/stores/org-store';

type CacheMode = 'on' | 'off' | 'cache-only';

type GetTokenFn = (options?: { cacheMode?: CacheMode }) => Promise<string>;

let _getAccessTokenSilently: GetTokenFn | null = null;

/**
 * Store the Auth0 getAccessTokenSilently function reference.
 * Called once during auth initialization from AuthProvider.
 */
export function setAuth0TokenProvider(fn: GetTokenFn): void {
  _getAccessTokenSilently = fn;
}

/**
 * Clear the stored reference (on logout / auth reset).
 */
export function clearAuth0TokenProvider(): void {
  _getAccessTokenSilently = null;
}

/**
 * Get a fresh Auth0 access token.
 * Returns null if the provider is not set (non-Auth0 users, or before init).
 *
 * Pass cacheMode: 'off' to force a fresh token bypassing the SDK cache
 * (e.g., on 401 retry per Auth0 best practice).
 */
export async function getAuth0AccessToken(options?: {
  cacheMode?: CacheMode;
}): Promise<string | null> {
  if (!_getAccessTokenSilently) {
    return null;
  }
  try {
    return await _getAccessTokenSilently(options);
  } catch (error) {
    console.error('Failed to get fresh Auth0 token:', error);
    Sentry.captureException(error, {
      extra: {
        context: 'auth0-token-refresh',
        cacheMode: options?.cacheMode,
      },
    });
    return null;
  }
}

/**
 * Get the current valid token, refreshing if needed for Auth0 users.
 *
 * - For OTP users: returns the Zustand store token directly (no Auth0 call).
 * - For Auth0 users: calls getAccessTokenSilently() to get a fresh token.
 *   Updates the Zustand store if the token was refreshed so other consumers stay in sync.
 *   Falls back to the store token if refresh fails (graceful degradation).
 */
export async function getFreshToken(options?: {
  cacheMode?: CacheMode;
}): Promise<string | null> {
  const { token, authType, setToken } = useOrgStore.getState();

  if (authType !== 'auth0') {
    return token;
  }

  const freshToken = await getAuth0AccessToken(options);
  if (freshToken) {
    if (freshToken !== token) {
      setToken(freshToken);
    }
    return freshToken;
  }

  // Graceful degradation: use existing store token if refresh fails
  return token;
}
